import { RotateCcw, ScrollText, SlidersHorizontal } from 'lucide-react';
import type { Competition } from '../../data/competitions';
import type { RulePack } from '../../types';

interface Props {
  comp: Competition;
  rulePack: RulePack;
  setRulePack: (pack: RulePack) => void;
}

/** Human-friendly labels for rule pack sections/fields. */
const SECTION_LABELS: Record<string, string> = {
  arena: 'Arena',
  robotLimits: 'Robot limits',
  match: 'Match settings',
  allowedParts: 'Allowed parts',
  scoring: 'Scoring',
  track: 'Track',
  maze: 'Maze',
};

function fieldLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

export default function RulesTab({ comp, rulePack, setRulePack }: Props) {
  const updateField = (section: string, key: string, value: number | boolean | string) => {
    setRulePack({
      ...rulePack,
      [section]: { ...rulePack[section], [key]: value },
    });
  };

  const resetToDefault = () => {
    setRulePack(JSON.parse(JSON.stringify(comp.rulePack)) as RulePack);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Official rules text */}
      <div className="space-y-5">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
          <ScrollText className="size-4" />
          {comp.name} rules
        </div>
        {comp.rulesText.map((section) => (
          <div key={section.section} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <h3 className={`text-base font-extrabold ${comp.text}`}>{section.section}</h3>
            <ul className="mt-3 space-y-2">
              {section.rules.map((r, i) => (
                <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-slate-300">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-slate-500" />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Editable rule pack */}
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
            <SlidersHorizontal className="size-4" />
            Rule pack — editable
          </div>
          <button
            type="button"
            onClick={resetToDefault}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-300 ring-1 ring-white/10 transition hover:text-white hover:ring-white/25"
          >
            <RotateCcw className="size-3.5" />
            Reset to Taa Standard
          </button>
        </div>
        <p className="text-sm leading-relaxed text-slate-400">
          Every school, club, or event can tune these numbers — Taa Sandbox does not hard-code rules. Your
          changes are saved on this device and used by the Simulate tab.
        </p>

        {Object.entries(rulePack).map(([section, fields]) => (
          <div key={section} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <h3 className="text-sm font-bold text-slate-200">{SECTION_LABELS[section] ?? section}</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {Object.entries(fields).map(([key, value]) => (
                <label key={key} className="flex flex-col gap-1 text-xs font-semibold text-slate-400">
                  {fieldLabel(key)}
                  {typeof value === 'boolean' ? (
                    <button
                      type="button"
                      onClick={() => updateField(section, key, !value)}
                      className={`mt-1 inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-bold ring-1 transition ${
                        value
                          ? `${comp.chip}`
                          : 'bg-white/5 text-slate-500 ring-white/10'
                      }`}
                    >
                      {value ? 'Allowed' : 'Not allowed'}
                    </button>
                  ) : typeof value === 'number' ? (
                    <input
                      type="number"
                      value={value}
                      onChange={(e) => updateField(section, key, Number(e.target.value))}
                      className="rounded-lg border border-white/10 bg-navy-950/60 px-2.5 py-1.5 text-sm font-medium text-slate-100 outline-none focus:border-cyan-400/40"
                    />
                  ) : (
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => updateField(section, key, e.target.value)}
                      className="rounded-lg border border-white/10 bg-navy-950/60 px-2.5 py-1.5 text-sm font-medium text-slate-100 outline-none focus:border-cyan-400/40"
                    />
                  )}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
